// Guia "Como ler o escritório": a ajuda completa, numa janela modal grande.
//
// Abre pela tecla '?', pelo botão de ajuda da barra lateral e pelo evento window
// 'agzos:painel' com detail 'ajuda'. Fecha no ✕, no Esc e clicando fora. Enquanto está
// aberto, as teclas não chegam ao escritório (o Esc fecha só o guia).
//
// Os textos ficam aqui, nos quatro idiomas (pt, en, es, fr), como o EXTRA do main.ts. O tipo
// Record<Lang, Textos> obriga os quatro a terem as mesmas seções. Os ícones das atividades
// vêm de iconeDoKind (i18n.ts), o mesmo do feed, então o guia não pode mostrar um ícone
// diferente do que a pessoa vê na tela.

import { getLang, iconeDoKind, type Lang, onLangChange } from './i18n.js';
import { carregaSobre, sobreSecaoGuia } from './about.js';

/** Estados do personagem, na ordem da seção Status. */
const STATUS_IDS = ['working', 'thinking', 'waiting', 'shell', 'idle', 'done', 'leaving'] as const;
/** Linhas da grade de atividades (ícone + nome). */
const ATIVIDADE_IDS = ['user', 'read', 'edit', 'write', 'search', 'bash', 'test', 'install', 'build', 'git', 'web', 'thinking', 'plan', 'task', 'reply', 'waiting', 'shell', 'done', 'error'] as const;
/** Animações das telas dos computadores (screenMode em world/behavior.ts). */
const TELA_IDS = ['read', 'write', 'terminal', 'web', 'thinking', 'done', 'alert', 'saver'] as const;
/** Painéis e páginas que a barra e o campus abrem. */
const PAINEL_IDS = ['dia', 'vivo', 'ponte', 'ajustes', 'acessos'] as const;

/** Ícone de cada animação de tela e de cada painel na seção correspondente. */
const ICONE_TELA: Record<TelaId, string> = { read: '📖', write: '✏️', terminal: '⌨️', web: '🌐', thinking: '💭', done: '✅', alert: '🔔', saver: '✨' };
const ICONE_PAINEL: Record<PainelId, string> = { dia: '📅', vivo: '🖥️', ponte: '🌉', ajustes: '⚙️', acessos: '🔑' };

export type StatusId = (typeof STATUS_IDS)[number];
export type AtividadeId = (typeof ATIVIDADE_IDS)[number];
type TelaId = (typeof TELA_IDS)[number];
type PainelId = (typeof PAINEL_IDS)[number];

/** Kind e texto que o iconeDoKind recebe para cada linha da grade. Waiting e shell têm marca própria. */
const ATIVIDADE_ICONE: Record<Exclude<AtividadeId, 'waiting' | 'shell'>, [kind: string, texto: string]> = {
  user: ['user', ''],
  read: ['read', ''],
  edit: ['write', 'editando um arquivo'],
  write: ['write', 'escrevendo um arquivo'],
  search: ['search', ''],
  bash: ['bash', 'rodando um comando'],
  test: ['test', ''],
  install: ['install', ''],
  build: ['build', ''],
  git: ['git', ''],
  web: ['web', ''],
  thinking: ['thinking', ''],
  plan: ['plan', ''],
  task: ['task', ''],
  reply: ['reply', ''],
  done: ['done', ''],
  error: ['error', ''],
};

/** Kinds que o iconeDoKind entende; o guia precisa listar todos (teste em tests/guide.test.ts). */
export const KINDS_ICONE = ['read', 'write', 'search', 'web', 'thinking', 'reply', 'user', 'error', 'task', 'plan', 'bash', 'done', 'test', 'install', 'build', 'git'] as const;

/** Kinds que a grade mostra (teste: têm de cobrir KINDS_ICONE). */
export const KINDS_GUIA: readonly string[] = Object.values(ATIVIDADE_ICONE).map(([kind]) => kind);

/** Ícone de uma linha da grade. Shell não tem ícone (a marca é a cor do status). */
export function iconeAtividade(id: AtividadeId): string {
  if (id === 'waiting') return '✋';
  if (id === 'shell') return '';
  const [kind, texto] = ATIVIDADE_ICONE[id];
  return iconeDoKind(kind, texto);
}

interface Textos {
  titulo: string;
  fechar: string;
  intro: { titulo: string; paragrafos: string[] };
  status: { titulo: string; lead: string; itens: Record<StatusId, [nome: string, texto: string]> };
  atividades: { titulo: string; lead: string; itens: Record<AtividadeId, string> };
  shell: { titulo: string; paragrafos: string[] };
  telas: {
    titulo: string;
    lead: string;
    itens: Record<TelaId, [nome: string, texto: string]>;
    lugares: { costas: [nome: string, texto: string]; frente: [nome: string, texto: string] };
    config: string;
  };
  baloes: { titulo: string; paragrafos: string[] };
  paineis: { titulo: string; itens: Record<PainelId, [nome: string, texto: string]> };
  atalhos: { titulo: string; lead: string; itens: Array<[tecla: string, texto: string]> };
}

/** Textos do guia por idioma. O tipo obriga pt, en, es e fr a terem as mesmas seções. */
export const TEXTOS_GUIA: Record<Lang, Textos> = {
  pt: {
    titulo: 'Como ler o escritório',
    fechar: 'Fechar',
    intro: {
      titulo: 'Introdução',
      paragrafos: [
        'Cada projeto aberto numa CLI (Claude Code, Codex, OpenCode, MiniMax ou Antigravity) vira uma sala. CLIs diferentes no mesmo projeto dividem a mesma sala.',
        'Cada sessão é um personagem, com nome próprio. Os subagentes chegam como colegas novos, trabalham na mesma sala e vão embora depois de alguns minutos parados. Esse tempo é ajustável em Ajustes → Servidor.',
        'Projeto sem irmão (sem outro projeto do mesmo grupo de nomes, como agzos-office e agzos-code) vai para o prédio Coworking.',
      ],
    },
    status: {
      titulo: 'Status',
      lead: 'O que o personagem está fazendo agora. As cores são as do ponto dele no escritório.',
      itens: {
        working: ['Trabalhando', 'Na mesa, digitando: processando um pedido, lendo, editando ou rodando comandos.'],
        thinking: ['Pensando', 'Na mesa, raciocinando ou escrevendo a resposta. Mesma cor do trabalhando.'],
        waiting: ['Precisa de você', 'Mão levantada: espera uma resposta sua (permissão, pergunta ou escolha). Dá para responder pelo escritório quando a Ponte está ligada.'],
        shell: ['Esperando o shell', 'Terminou o turno e espera um comando em segundo plano. Veja a seção abaixo.'],
        idle: ['Ocioso', 'Circula pelo escritório, vai à copa e ao lounge e joga com os colegas: é a vida social.'],
        done: ['Concluído', 'Subagente que terminou o trabalho. Aparece um ✓ na tela da mesa antes de ele sair.'],
        leaving: ['Saindo', 'A sessão foi fechada, e o personagem vai saindo aos poucos.'],
      },
    },
    atividades: {
      titulo: 'Atividades',
      lead: 'Cada linha do feed e cada balão começa com um ícone, de acordo com o tipo da atividade.',
      itens: {
        user: 'recebeu um pedido',
        read: 'lendo arquivo',
        edit: 'editando',
        write: 'escrevendo arquivo',
        search: 'buscando no código',
        bash: 'comando no terminal',
        test: 'rodando testes',
        install: 'instalando',
        build: 'compilando',
        git: 'git',
        web: 'pesquisando na web',
        thinking: 'pensando',
        plan: 'organizando tarefas',
        task: 'chamando subagentes',
        reply: 'escrevendo a resposta',
        waiting: 'precisa de você',
        shell: 'esperando um shell',
        done: 'concluiu',
        error: 'algo deu errado',
      },
    },
    shell: {
      titulo: 'Esperando o shell',
      paragrafos: [
        'O turno acabou, mas ficou um processo rodando em segundo plano (um servidor de desenvolvimento, por exemplo). O personagem fica azul, e a tela da mesa mostra o protetor de tela.',
        'O mesmo status aparece quando um comando fica parado por mais de 10 s sem terminar.',
      ],
    },
    telas: {
      titulo: 'As telas dos computadores',
      lead: 'Cada mesa tem um monitor. A animação mostra o que o agente está fazendo:',
      itens: {
        read: ['Lendo', 'texto rolando para cima.'],
        write: ['Editando ou escrevendo', 'linhas aparecendo uma a uma, com o cursor piscando no fim.'],
        terminal: ['Terminal', 'fundo escuro, prompt verde e saída subindo. Usado por comandos, testes, instalações, builds e git.'],
        web: ['Web', 'janela de navegador com a barra carregando, na pesquisa ou na consulta.'],
        thinking: ['Pensando', 'três pontinhos acendendo um de cada vez.'],
        done: ['Concluiu', 'um ✓ verde no centro da tela, por alguns segundos.'],
        alert: ['Esperando você', 'a tela pisca em âmbar: precisa da sua atenção.'],
        saver: ['Ocioso', 'protetor de tela escuro, com três estrelinhas acendendo devagar.'],
      },
      lugares: {
        costas: ['De costas (fileira de baixo)', 'O personagem senta de costas para você, e a tela fica virada para a câmera, visível.'],
        frente: ['De frente (fileira de cima)', 'O personagem senta de frente para a tela. Você vê o monitor por trás, sem tela.'],
      },
      config: 'Em Ajustes → Tema, as telas podem ficar animadas, em quadro fixo (economiza CPU) ou apagadas.',
    },
    baloes: {
      titulo: 'Balões e cartão',
      paragrafos: [
        'O balão de fala fica acima do nome do personagem, com a última atividade: "📖 Lendo…" ou "✅ Concluiu em 22s". Quando ele precisa de você, o balão amarelo diz "Esperando você".',
        'Ao passar o mouse sobre um personagem, aparece o cartão de detalhes: nome, CLI, projeto, status e última atividade.',
        'Os dois podem ser desligados em Ajustes → Tema: "Balões de fala com a atividade" e "Cartão de detalhes ao passar o mouse". O "Esperando você" continua aparecendo mesmo com os balões desligados.',
      ],
    },
    paineis: {
      titulo: 'Painéis',
      itens: {
        dia: ['Meu dia', 'Resumo do dia: tempo por status, projetos que mais trabalharam, tokens e pedidos de aprovação. Dá para voltar a outros dias. O Timelapse reproduz o dia inteiro em três velocidades.'],
        vivo: ['Terminal ao vivo', 'A conversa do agente, atualizada enquanto acontece. Filtros: Tudo, Só prompts e Sem ferramentas; a busca (Ctrl+F) acha um trecho. Dá para maximizar ou minimizar a janela. Assumir (só admin, com Claude Code) abre a sessão num terminal para você continuar nela; não use os dois ao mesmo tempo. Encerrar (só admin) fecha o terminal que o escritório abriu. O botão Terminal da barra lista essas sessões e cria novas.'],
        ponte: ['Ponte', 'Por CLI, liga ou desliga aprovar pedidos e mandar mensagens pelo escritório. Com a Ponte ligada, o pedido ou a pergunta do agente vira um cartão no painel dele, com botões para aprovar, recusar ou responder. Aprovar pelo escritório exige TOTP no login.'],
        ajustes: ['Ajustes', 'Página de configuração, no topo. Tema (estação, noite, idioma, título, balões, cartão, telas e atualização do terminal), Servidor (tempo do subagente parado), CLIs, Personagens, Prédios, Ponte, Backup e Acessos.'],
        acessos: ['Acessos', 'Em Ajustes, você convida pessoas. O convite define o papel: "Só o escritório" (vê o campus e baixa o instalador) ou "Escritório + CMS" (admin, faz tudo, inclusive convidar). O link aparece uma vez, para copiar, e vale uma vez.'],
      },
    },
    atalhos: {
      titulo: 'Atalhos de teclado',
      lead: 'Não funcionam enquanto você digita num campo de texto.',
      itens: [
        ['/', 'busca agente, projeto ou CLI'],
        ['j', 'próximo agente da lateral'],
        ['k', 'agente anterior da lateral'],
        ['↑ ↓', 'anda pela lista de agentes da lateral'],
        ['Enter', 'no feed, seleciona o agente da linha'],
        ['f', 'segue o agente selecionado com a câmera'],
        ['t', 'abre o terminal ao vivo do agente selecionado'],
        ['?', 'mostra ou esconde este guia'],
        ['O', 'visão geral: enquadra o campus inteiro'],
        ['0', 'também visão geral'],
        ['+ / −', 'aproxima e afasta'],
        ['M', 'abre o Meu dia'],
        ['E', 'abre as Entregas'],
        ['H', 'abre o Histórico de sessões'],
        ['L', 'abre ou fecha o timelapse do dia'],
        ['P', 'vai ao próximo pedido de aprovação ou pergunta, com o cartão aberto'],
        ['[', 'mostra ou esconde a lateral'],
        [']', 'mostra ou esconde o feed de atividade'],
        ['← → ↑ ↓', 'move a câmera uma casa por toque; segure para andar (fora da lista da lateral)'],
        ['W A S D', 'o mesmo, com as letras'],
        ['Esc', 'fecha o guia ou o painel aberto; sem nada aberto, tira a seleção do agente'],
      ],
    },
  },
  en: {
    titulo: 'How to read the office',
    fechar: 'Close',
    intro: {
      titulo: 'Introduction',
      paragrafos: [
        'Each project opened in a CLI (Claude Code, Codex, OpenCode, MiniMax or Antigravity) becomes a room. Different CLIs in the same project share the same room.',
        'Each session is a character with its own name. Subagents arrive as new colleagues, work in the same room and leave after a few minutes idle. That time can be adjusted in Settings → Server.',
        'A project with no siblings (no other project from the same name group, such as agzos-office and agzos-code) goes to the Coworking building.',
      ],
    },
    status: {
      titulo: 'Status',
      lead: 'What the character is doing right now. The colours are the ones of its dot in the office.',
      itens: {
        working: ['Working', 'At the desk, typing: handling a request, reading, editing or running commands.'],
        thinking: ['Thinking', 'At the desk, reasoning or writing the answer. Same colour as working.'],
        waiting: ['Needs you', 'Hand raised: waiting for your answer (permission, question or choice). You can answer from the office when the Bridge is on.'],
        shell: ['Waiting for shell', 'Finished the turn and is waiting on a background command. See the section below.'],
        idle: ['Idle', 'Wanders the office, goes to the pantry and the lounge and plays with colleagues: the social life.'],
        done: ['Done', 'A subagent that finished its work. A ✓ appears on the desk screen before it leaves.'],
        leaving: ['Leaving', 'The session was closed, and the character is leaving little by little.'],
      },
    },
    atividades: {
      titulo: 'Activities',
      lead: 'Each feed line and each balloon starts with an icon, according to the type of activity.',
      itens: {
        user: 'received a request',
        read: 'reading a file',
        edit: 'editing',
        write: 'writing a file',
        search: 'searching the code',
        bash: 'terminal command',
        test: 'running tests',
        install: 'installing',
        build: 'building',
        git: 'git',
        web: 'searching the web',
        thinking: 'thinking',
        plan: 'organizing tasks',
        task: 'calling subagents',
        reply: 'writing the answer',
        waiting: 'needs you',
        shell: 'waiting for shell',
        done: 'finished',
        error: 'something went wrong',
      },
    },
    shell: {
      titulo: 'Waiting for shell',
      paragrafos: [
        'The turn ended, but a process is still running in the background (a development server, for example). The character turns blue, and the desk screen shows the screensaver.',
        'The same status appears when a command stays stuck for more than 10 s without finishing.',
      ],
    },
    telas: {
      titulo: 'The computer screens',
      lead: 'Each desk has a monitor. The animation shows what the agent is doing:',
      itens: {
        read: ['Reading', 'text scrolling up.'],
        write: ['Editing or writing', 'lines appearing one by one, with the cursor blinking at the end.'],
        terminal: ['Terminal', 'dark background, green prompt and output scrolling. Used by commands, tests, installs, builds and git.'],
        web: ['Web', 'browser window with the loading bar, when searching or looking something up.'],
        thinking: ['Thinking', 'three dots lighting up one at a time.'],
        done: ['Finished', 'a green ✓ in the middle of the screen, for a few seconds.'],
        alert: ['Waiting for you', 'the screen blinks amber: it needs your attention.'],
        saver: ['Idle', 'dark screensaver, with three little stars lighting up slowly.'],
      },
      lugares: {
        costas: ['Facing away (bottom row)', 'The character sits with its back to you, and the screen faces the camera, visible.'],
        frente: ['Facing the screen (top row)', 'The character sits facing the screen. You see the monitor from behind, with no screen.'],
      },
      config: 'In Settings → Theme, the screens can be animated, still (saves CPU) or off.',
    },
    baloes: {
      titulo: 'Balloons and card',
      paragrafos: [
        'The speech balloon sits above the character\'s name, with the last activity: "📖 Reading…" or "✅ Finished in 22s". When it needs you, the yellow balloon says "Waiting for you".',
        'Hovering over a character shows the details card: name, CLI, project, status and last activity.',
        'Both can be turned off in Settings → Theme: "Activity speech balloons" and "Details card on hover". "Waiting for you" still shows with the balloons off.',
      ],
    },
    paineis: {
      titulo: 'Panels',
      itens: {
        dia: ['My day', 'Summary of the day: time per status, the busiest projects, tokens and approval requests. You can go back to other days. The Timelapse replays the whole day at three speeds.'],
        vivo: ['Live terminal', 'The agent\'s conversation, updated as it happens. Filters: All, Prompts only and No tools; search (Ctrl+F) finds a passage. You can maximize or minimize the window. Take over (admin only, with Claude Code) opens the session in a terminal so you can carry on in it; don\'t use both at once. End (admin only) closes the terminal the office opened. The Terminal button in the top bar lists these sessions and creates new ones.'],
        ponte: ['Bridge', 'For each CLI, turns approving requests and sending messages from the office on or off. With the Bridge on, the agent\'s request or question becomes a card in its panel, with buttons to approve, decline or answer. Approving from the office requires TOTP at login.'],
        ajustes: ['Settings', 'Configuration page, in the top bar. Theme (season, night, language, title, balloons, card, screens and live refresh), Server (time an idle subagent stays), CLIs, Characters, Buildings, Bridge, Backup and Access.'],
        acessos: ['Access', 'In Settings, you invite people. The invite sets the role: "Office only" (sees the campus and downloads the installer) or "Office + CMS" (admin, can do everything, including inviting). The link is shown once, to copy, and works once.'],
      },
    },
    atalhos: {
      titulo: 'Keyboard shortcuts',
      lead: 'They do not work while you are typing in a text field.',
      itens: [
        ['/', 'search agent, project or CLI'],
        ['j', 'next agent in the sidebar'],
        ['k', 'previous agent in the sidebar'],
        ['↑ ↓', 'move through the agent list in the sidebar'],
        ['Enter', 'in the feed, selects the agent of that line'],
        ['f', 'follow the selected agent with the camera'],
        ['t', 'open the live terminal of the selected agent'],
        ['?', 'show or hide this guide'],
        ['O', 'overview: fit the whole campus'],
        ['0', 'also overview'],
        ['+ / −', 'zoom in and out'],
        ['M', 'open My day'],
        ['E', 'open Deliveries'],
        ['H', 'open Session history'],
        ['L', 'opens or closes the day timelapse'],
        ['P', 'goes to the next approval or question, with its card open'],
        ['[', 'shows or hides the sidebar'],
        [']', 'shows or hides the activity feed'],
        ['← → ↑ ↓', 'moves the camera one tile per tap; hold to keep walking (outside the sidebar list)'],
        ['W A S D', 'the same, with letters'],
        ['Esc', 'closes the guide or the open panel; with nothing open, clears the agent selection'],
      ],
    },
  },
  es: {
    titulo: 'Cómo leer la oficina',
    fechar: 'Cerrar',
    intro: {
      titulo: 'Introducción',
      paragrafos: [
        'Cada proyecto abierto en una CLI (Claude Code, Codex, OpenCode, MiniMax o Antigravity) se convierte en una sala. Las CLI distintas en el mismo proyecto comparten la misma sala.',
        'Cada sesión es un personaje con nombre propio. Los subagentes llegan como compañeros nuevos, trabajan en la misma sala y se van después de unos minutos inactivos. Ese tiempo se ajusta en Ajustes → Servidor.',
        'Un proyecto sin hermanos (sin otro proyecto del mismo grupo de nombres, como agzos-office y agzos-code) va al edificio Coworking.',
      ],
    },
    status: {
      titulo: 'Estado',
      lead: 'Lo que el personaje está haciendo ahora. Los colores son los de su punto en la oficina.',
      itens: {
        working: ['Trabajando', 'En la mesa, escribiendo: atendiendo un pedido, leyendo, editando o ejecutando comandos.'],
        thinking: ['Pensando', 'En la mesa, razonando o escribiendo la respuesta. Mismo color que trabajando.'],
        waiting: ['Necesita de ti', 'Mano levantada: espera tu respuesta (permiso, pregunta o elección). Puedes responder desde la oficina cuando el Puente está activado.'],
        shell: ['Esperando el shell', 'Terminó el turno y espera un comando en segundo plano. Mira la sección de abajo.'],
        idle: ['Inactivo', 'Pasea por la oficina, va a la cocina y al lounge y juega con los compañeros: la vida social.'],
        done: ['Terminado', 'Subagente que terminó el trabajo. Aparece un ✓ en la pantalla de la mesa antes de irse.'],
        leaving: ['Saliendo', 'La sesión se cerró y el personaje se va poco a poco.'],
      },
    },
    atividades: {
      titulo: 'Actividades',
      lead: 'Cada línea del feed y cada globo empieza con un icono, según el tipo de actividad.',
      itens: {
        user: 'recibió un pedido',
        read: 'leyendo un archivo',
        edit: 'editando',
        write: 'escribiendo un archivo',
        search: 'buscando en el código',
        bash: 'comando en la terminal',
        test: 'ejecutando pruebas',
        install: 'instalando',
        build: 'compilando',
        git: 'git',
        web: 'buscando en la web',
        thinking: 'pensando',
        plan: 'organizando tareas',
        task: 'llamando subagentes',
        reply: 'escribiendo la respuesta',
        waiting: 'necesita de ti',
        shell: 'esperando el shell',
        done: 'terminó',
        error: 'algo salió mal',
      },
    },
    shell: {
      titulo: 'Esperando el shell',
      paragrafos: [
        'El turno terminó, pero quedó un proceso ejecutándose en segundo plano (un servidor de desarrollo, por ejemplo). El personaje se pone azul y la pantalla de la mesa muestra el salvapantallas.',
        'El mismo estado aparece cuando un comando se queda parado más de 10 s sin terminar.',
      ],
    },
    telas: {
      titulo: 'Las pantallas de las computadoras',
      lead: 'Cada mesa tiene un monitor. La animación muestra lo que hace el agente:',
      itens: {
        read: ['Leyendo', 'texto que sube.'],
        write: ['Editando o escribiendo', 'líneas que aparecen una a una, con el cursor parpadeando al final.'],
        terminal: ['Terminal', 'fondo oscuro, prompt verde y salida que sube. Lo usan comandos, pruebas, instalaciones, builds y git.'],
        web: ['Web', 'ventana del navegador con la barra de carga, al buscar o consultar.'],
        thinking: ['Pensando', 'tres puntitos que se encienden de a uno.'],
        done: ['Terminó', 'un ✓ verde en el centro de la pantalla, por unos segundos.'],
        alert: ['Esperando a ti', 'la pantalla parpadea en ámbar: necesita tu atención.'],
        saver: ['Inactivo', 'salvapantallas oscuro, con tres estrellitas que se encienden despacio.'],
      },
      lugares: {
        costas: ['De espaldas (fila de abajo)', 'El personaje se sienta de espaldas a ti, y la pantalla mira a la cámara, visible.'],
        frente: ['De frente (fila de arriba)', 'El personaje se sienta de frente a la pantalla. Ves el monitor por detrás, sin pantalla.'],
      },
      config: 'En Ajustes → Tema, las pantallas pueden estar animadas, en cuadro fijo (ahorra CPU) o apagadas.',
    },
    baloes: {
      titulo: 'Globos y tarjeta',
      paragrafos: [
        'El globo de diálogo aparece sobre el nombre del personaje, con la última actividad: "📖 Leyendo…" o "✅ Terminó en 22s". Cuando necesita de ti, el globo amarillo dice "Esperando a ti".',
        'Al pasar el ratón sobre un personaje aparece la tarjeta de detalles: nombre, CLI, proyecto, estado y última actividad.',
        'Ambos se pueden desactivar en Ajustes → Tema: "Globos de diálogo con la actividad" y "Tarjeta de detalles al pasar el ratón". "Esperando a ti" sigue apareciendo aunque los globos estén desactivados.',
      ],
    },
    paineis: {
      titulo: 'Paneles',
      itens: {
        dia: ['Mi día', 'Resumen del día: tiempo por estado, proyectos que más trabajaron, tokens y pedidos de aprobación. Puedes volver a otros días. El Timelapse reproduce el día entero en tres velocidades.'],
        vivo: ['Terminal en vivo', 'La conversación del agente, actualizada mientras ocurre. Filtros: Todo, Solo prompts y Sin herramientas; la búsqueda (Ctrl+F) encuentra un fragmento. Puedes maximizar o minimizar la ventana. Asumir (solo admin, con Claude Code) abre la sesión en una terminal para seguir en ella; no uses las dos a la vez. Terminar (solo admin) cierra la terminal que abrió la oficina. El botón Terminal de la barra lista estas sesiones y crea nuevas.'],
        ponte: ['Puente', 'Por CLI, activa o desactiva aprobar pedidos y enviar mensajes desde la oficina. Con el Puente activado, el pedido o la pregunta del agente se convierte en una tarjeta en su panel, con botones para aprobar, rechazar o responder. Aprobar desde la oficina exige TOTP al iniciar sesión.'],
        ajustes: ['Ajustes', 'Página de configuración, arriba. Tema (estación, noche, idioma, título, globos, tarjeta, pantallas y actualización del terminal), Servidor (tiempo del subagente inactivo), CLI, Personajes, Edificios, Puente, Copia de seguridad y Accesos.'],
        acessos: ['Accesos', 'En Ajustes, invitas a personas. La invitación define el papel: "Solo la oficina" (ve el campus y descarga el instalador) o "Oficina + CMS" (admin, puede hacer todo, incluido invitar). El enlace aparece una vez, para copiar, y vale una vez.'],
      },
    },
    atalhos: {
      titulo: 'Atajos de teclado',
      lead: 'No funcionan mientras escribes en un campo de texto.',
      itens: [
        ['/', 'buscar agente, proyecto o CLI'],
        ['j', 'siguiente agente de la lista lateral'],
        ['k', 'agente anterior de la lista lateral'],
        ['↑ ↓', 'recorre la lista de agentes de la lista lateral'],
        ['Enter', 'en el feed, selecciona el agente de esa línea'],
        ['f', 'sigue al agente seleccionado con la cámara'],
        ['t', 'abre la terminal en vivo del agente seleccionado'],
        ['?', 'muestra u oculta esta guía'],
        ['O', 'vista general: encuadra todo el campus'],
        ['0', 'también vista general'],
        ['+ / −', 'acerca y aleja'],
        ['M', 'abre Mi día'],
        ['E', 'abre las Entregas'],
        ['H', 'abre el Historial de sesiones'],
        ['L', 'abre o cierra el timelapse del día'],
        ['P', 'va al próximo pedido de aprobación o pregunta, con la tarjeta abierta'],
        ['[', 'muestra u oculta la barra lateral'],
        [']', 'muestra u oculta el feed de actividad'],
        ['← → ↑ ↓', 'mueve la cámara una casilla por toque; mantén para seguir (fuera de la lista lateral)'],
        ['W A S D', 'lo mismo, con letras'],
        ['Esc', 'cierra la guía o el panel abierto; sin nada abierto, quita la selección del agente'],
      ],
    },
  },
  fr: {
    titulo: 'Comment lire le bureau',
    fechar: 'Fermer',
    intro: {
      titulo: 'Introduction',
      paragrafos: [
        'Chaque projet ouvert dans une CLI (Claude Code, Codex, OpenCode, MiniMax ou Antigravity) devient une salle. Des CLI différentes sur le même projet partagent la même salle.',
        'Chaque session est un personnage avec son propre nom. Les sous-agents arrivent comme de nouveaux collègues, travaillent dans la même salle et partent après quelques minutes sans activité. Ce délai se règle dans Réglages → Serveur.',
        'Un projet sans frère (sans autre projet du même groupe de noms, comme agzos-office et agzos-code) va dans le bâtiment Coworking.',
      ],
    },
    status: {
      titulo: 'Statut',
      lead: 'Ce que fait le personnage en ce moment. Les couleurs sont celles de son point dans le bureau.',
      itens: {
        working: ['En cours', 'Au bureau, en train de taper : il traite une demande, lit, modifie ou lance des commandes.'],
        thinking: ['Réflexion', 'Au bureau, il raisonne ou rédige la réponse. Même couleur que « En cours ».'],
        waiting: ['A besoin de vous', 'Main levée : il attend votre réponse (permission, question ou choix). Vous pouvez répondre depuis le bureau quand le Pont est activé.'],
        shell: ['Attend le shell', 'Il a terminé le tour et attend une commande en arrière-plan. Voir la section ci-dessous.'],
        idle: ['Inactif', 'Il se promène dans le bureau, va à la cuisine et au salon, et joue avec ses collègues : la vie sociale.'],
        done: ['Terminé', 'Sous-agent qui a fini son travail. Un ✓ apparaît sur l’écran du bureau avant son départ.'],
        leaving: ['Départ', 'La session a été fermée, et le personnage s’en va petit à petit.'],
      },
    },
    atividades: {
      titulo: 'Activités',
      lead: 'Chaque ligne du fil et chaque bulle commence par une icône, selon le type d’activité.',
      itens: {
        user: 'a reçu une demande',
        read: 'lit un fichier',
        edit: 'modifie',
        write: 'écrit un fichier',
        search: 'cherche dans le code',
        bash: 'commande dans le terminal',
        test: 'lance les tests',
        install: 'installe',
        build: 'compile',
        git: 'git',
        web: 'recherche sur le web',
        thinking: 'réfléchit',
        plan: 'organise les tâches',
        task: 'appelle des sous-agents',
        reply: 'rédige la réponse',
        waiting: 'a besoin de vous',
        shell: 'attend le shell',
        done: 'a terminé',
        error: 'quelque chose a échoué',
      },
    },
    shell: {
      titulo: 'Attend le shell',
      paragrafos: [
        'Le tour est terminé, mais un processus tourne encore en arrière-plan (un serveur de développement, par exemple). Le personnage devient bleu et l’écran du bureau montre l’économiseur d’écran.',
        'Le même statut apparaît quand une commande reste bloquée plus de 10 s sans se terminer.',
      ],
    },
    telas: {
      titulo: 'Les écrans des ordinateurs',
      lead: 'Chaque bureau a un moniteur. L’animation montre ce que fait l’agent :',
      itens: {
        read: ['Lecture', 'texte qui défile vers le haut.'],
        write: ['Modification ou écriture', 'des lignes apparaissent une à une, avec le curseur qui clignote à la fin.'],
        terminal: ['Terminal', 'fond sombre, invite verte et sortie qui défile. Utilisé par les commandes, tests, installations, builds et git.'],
        web: ['Web', 'fenêtre de navigateur avec la barre de chargement, pendant une recherche ou une consultation.'],
        thinking: ['Réflexion', 'trois points qui s’allument un à un.'],
        done: ['Terminé', 'un ✓ vert au centre de l’écran, pendant quelques secondes.'],
        alert: ['Vous attend', 'l’écran clignote en orange : il a besoin de votre attention.'],
        saver: ['Inactif', 'économiseur d’écran sombre, avec trois petites étoiles qui s’allument lentement.'],
      },
      lugares: {
        costas: ['De dos (rangée du bas)', 'Le personnage est assis de dos à vous, et l’écran est tourné vers la caméra, visible.'],
        frente: ['De face (rangée du haut)', 'Le personnage est assis face à l’écran. Vous voyez le moniteur de dos, sans écran.'],
      },
      config: 'Dans Réglages → Thème, les écrans peuvent être animés, fixes (économise le CPU) ou éteints.',
    },
    baloes: {
      titulo: 'Bulles et carte',
      paragrafos: [
        'La bulle de dialogue apparaît au-dessus du nom du personnage, avec la dernière activité : « 📖 Lecture… » ou « ✅ Terminé en 22 s ». Quand il a besoin de vous, la bulle jaune dit « Vous attend ».',
        'En passant la souris sur un personnage, la carte de détails apparaît : nom, CLI, projet, statut et dernière activité.',
        'Les deux peuvent être désactivés dans Réglages → Thème : « Bulles de dialogue avec l’activité » et « Carte de détails au survol ». « Vous attend » reste affiché même avec les bulles désactivées.',
      ],
    },
    paineis: {
      titulo: 'Panneaux',
      itens: {
        dia: ['Mon jour', 'Résumé de la journée : temps par statut, projets les plus actifs, jetons et demandes d’approbation. Vous pouvez revenir à d’autres jours. Le Timelapse rejoue la journée entière à trois vitesses.'],
        vivo: ['Terminal en direct', 'La conversation de l’agent, mise à jour au fil de l’eau. Filtres : Tout, Prompts seulement et Sans outils ; la recherche (Ctrl+F) trouve un passage. Vous pouvez agrandir ou réduire la fenêtre. Reprendre (admin seulement, avec Claude Code) ouvre la session dans un terminal pour la poursuivre ; n’utilisez pas les deux en même temps. Terminer (admin seulement) ferme le terminal ouvert par le bureau. Le bouton Terminal de la barre liste ces sessions et en crée de nouvelles.'],
        ponte: ['Pont', 'Par CLI, active ou désactive l’approbation des demandes et l’envoi de messages depuis le bureau. Pont activé, la demande ou la question de l’agent devient une carte dans son panneau, avec des boutons pour approuver, refuser ou répondre. Approuver depuis le bureau exige un TOTP à la connexion.'],
        ajustes: ['Réglages', 'Page de configuration, en haut. Thème (saison, nuit, langue, titre, bulles, carte, écrans et actualisation du terminal), Serveur (délai du sous-agent inactif), CLI, Personnages, Bâtiments, Pont, Sauvegarde et Accès.'],
        acessos: ['Accès', 'Dans Réglages, vous invitez des personnes. L’invitation définit le rôle : « Bureau seulement » (voit le campus et télécharge l’installateur) ou « Bureau + CMS » (admin, peut tout faire, y compris inviter). Le lien s’affiche une fois, pour être copié, et ne sert qu’une fois.'],
      },
    },
    atalhos: {
      titulo: 'Raccourcis clavier',
      lead: 'Ils ne fonctionnent pas pendant que vous écrivez dans un champ de texte.',
      itens: [
        ['/', 'chercher un agent, un projet ou une CLI'],
        ['j', 'agent suivant de la liste latérale'],
        ['k', 'agent précédent de la liste latérale'],
        ['↑ ↓', 'parcourir la liste des agents de la liste latérale'],
        ['Enter', 'dans le fil, sélectionne l’agent de cette ligne'],
        ['f', 'suivre l’agent sélectionné avec la caméra'],
        ['t', 'ouvrir le terminal en direct de l’agent sélectionné'],
        ['?', 'afficher ou masquer ce guide'],
        ['O', 'vue d’ensemble : cadre tout le campus'],
        ['0', 'aussi vue d’ensemble'],
        ['+ / −', 'zoom avant et arrière'],
        ['M', 'ouvre Ma journée'],
        ['E', 'ouvre les Livraisons'],
        ['H', 'ouvre l’Historique des sessions'],
        ['L', 'ouvre ou ferme la vidéo accélérée de la journée'],
        ['P', 'va à la prochaine demande d’approbation ou question, carte ouverte'],
        ['[', 'affiche ou masque la barre latérale'],
        [']', 'affiche ou masque le fil d’activité'],
        ['← → ↑ ↓', 'déplace la caméra d’une case par pression ; maintenez pour continuer (hors de la liste latérale)'],
        ['W A S D', 'idem, avec les lettres'],
        ['Esc', 'ferme le guide ou le panneau ouvert ; sans rien d’ouvert, retire la sélection de l’agent'],
      ],
    },
  },
};

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (valor: string): string => valor.replace(/[&<>"']/g, (c) => ESC[c] ?? c);

/** HTML da janela inteira (cabeçalho e corpo) no idioma pedido. Função pura, para o teste e para pintar. */
export function renderizaGuia(lang: Lang): string {
  const g = TEXTOS_GUIA[lang];
  const secao = (id: string, titulo: string, corpo: string) =>
    `<section class="guia-sec" aria-labelledby="guia-${id}"><h3 id="guia-${id}">${esc(titulo)}</h3>${corpo}</section>`;
  const paragrafos = (lista: string[]) => lista.map((p) => `<p>${esc(p)}</p>`).join('');
  const item = (icone: string, nome: string, texto: string) =>
    `<li><span class="guia-ico" aria-hidden="true">${icone}</span><div><strong>${esc(nome)}</strong>${texto ? `<p class="guia-txt">${esc(texto)}</p>` : ''}</div></li>`;

  const intro = secao('intro', g.intro.titulo, paragrafos(g.intro.paragrafos));

  const status = secao(
    'status',
    g.status.titulo,
    `<p class="guia-lead">${esc(g.status.lead)}</p><ul class="guia-lista guia-status">${STATUS_IDS.map((id) => {
      const [nome, texto] = g.status.itens[id];
      return `<li><span class="guia-cor st-${id}" aria-hidden="true"></span><div><strong>${esc(nome)}</strong><p class="guia-txt">${esc(texto)}</p></div></li>`;
    }).join('')}</ul>`,
  );

  const atividades = secao(
    'atividades',
    g.atividades.titulo,
    `<p class="guia-lead">${esc(g.atividades.lead)}</p><ul class="guia-lista guia-atividades">${ATIVIDADE_IDS.map((id) => {
      const icone = id === 'shell' ? '<span class="guia-cor st-shell"></span>' : iconeAtividade(id);
      return `<li><span class="guia-ico" aria-hidden="true">${icone}</span><span>${esc(g.atividades.itens[id])}</span></li>`;
    }).join('')}</ul>`,
  );

  const shell = secao('shell', g.shell.titulo, paragrafos(g.shell.paragrafos));

  const telas = secao(
    'telas',
    g.telas.titulo,
    `<p class="guia-lead">${esc(g.telas.lead)}</p><ul class="guia-lista guia-status">${TELA_IDS.map((id) => item(ICONE_TELA[id], g.telas.itens[id][0], g.telas.itens[id][1])).join('')}</ul>` +
      `<h4 class="guia-sub">${esc(g.telas.lugares.costas[0])}</h4><p>${esc(g.telas.lugares.costas[1])}</p>` +
      `<h4 class="guia-sub">${esc(g.telas.lugares.frente[0])}</h4><p>${esc(g.telas.lugares.frente[1])}</p>` +
      `<p class="guia-txt">${esc(g.telas.config)}</p>`,
  );

  const baloes = secao('baloes', g.baloes.titulo, paragrafos(g.baloes.paragrafos));

  const paineis = secao(
    'paineis',
    g.paineis.titulo,
    `<ul class="guia-lista guia-status">${PAINEL_IDS.map((id) => item(ICONE_PAINEL[id], g.paineis.itens[id][0], g.paineis.itens[id][1])).join('')}</ul>`,
  );

  const atalhos = secao(
    'atalhos',
    g.atalhos.titulo,
    `<p class="guia-lead">${esc(g.atalhos.lead)}</p><dl class="guia-teclas">${g.atalhos.itens.map(([tecla, texto]) => `<dt><kbd>${esc(tecla)}</kbd></dt><dd>${esc(texto)}</dd>`).join('')}</dl>`,
  );

  return `<div class="guia-caixa" role="dialog" aria-modal="true" aria-labelledby="guia-titulo">
    <header class="guia-cab"><h2 id="guia-titulo">${esc(g.titulo)}</h2><button type="button" class="guia-fechar" data-guia-fechar aria-label="${esc(g.fechar)}">✕</button></header>
    <div class="guia-corpo">${intro}${status}${atividades}${shell}${telas}${baloes}${paineis}${atalhos}${sobreSecaoGuia(lang)}</div>
  </div>`;
}

// ------------------------------------------------------------------ janela

let raiz: HTMLElement | undefined;
/** Quem tinha o foco antes do guia abrir: o foco volta para ele ao fechar. */
let focoAnterior: HTMLElement | null = null;

export function tituloGuia(): string {
  return TEXTOS_GUIA[getLang()].titulo;
}

export function guiaAberto(): boolean {
  return raiz !== undefined && !raiz.hidden;
}

/** Repinta o guia (ao trocar o idioma), mantendo a rolagem. */
function pinta(): void {
  if (!raiz) return;
  const rolagem = raiz.querySelector<HTMLElement>('.guia-corpo')?.scrollTop ?? 0;
  raiz.innerHTML = renderizaGuia(getLang());
  const corpo = raiz.querySelector<HTMLElement>('.guia-corpo');
  if (corpo) corpo.scrollTop = rolagem;
}

export function abreGuia(): void {
  montaGuia();
  if (!raiz || guiaAberto()) return;
  focoAnterior = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  pinta();
  raiz.hidden = false;
  raiz.querySelector<HTMLButtonElement>('[data-guia-fechar]')?.focus();
  // a versão e o commit chegam por GET /api/sobre: pinta de novo quando chegam (ou falham)
  void carregaSobre().then(() => {
    if (!raiz || !guiaAberto()) return;
    const focoDentro = raiz.contains(document.activeElement);
    pinta();
    if (focoDentro) raiz.querySelector<HTMLButtonElement>('[data-guia-fechar]')?.focus();
  });
}

export function fechaGuia(): void {
  if (!raiz || !guiaAberto()) return;
  raiz.hidden = true;
  focoAnterior?.focus();
  focoAnterior = null;
}

/** Teclas com o guia aberto: Esc e '?' fecham; o resto não chega ao escritório atrás. */
function teclaComGuia(e: KeyboardEvent): void {
  if (!guiaAberto()) return;
  e.stopPropagation();
  if (e.key === 'Escape' || e.key === '?') fechaGuia();
}

let montado = false;

/** Cria a janela uma vez e liga os gatilhos: clique fora ou no ✕, teclas e o evento 'agzos:painel'. */
export function montaGuia(): void {
  if (montado) return;
  montado = true;
  raiz = document.createElement('div');
  raiz.id = 'guia';
  raiz.className = 'guia';
  raiz.hidden = true;
  document.body.append(raiz);

  raiz.addEventListener('click', (event) => {
    const alvo = event.target as HTMLElement;
    if (alvo === raiz || alvo.closest('[data-guia-fechar]')) fechaGuia();
  });
  document.addEventListener('keydown', teclaComGuia, true);
  window.addEventListener('agzos:painel', (event) => {
    if ((event as CustomEvent<string>).detail === 'ajuda') abreGuia();
  });
  onLangChange(() => {
    if (guiaAberto()) pinta();
  });
}
